// pages/ArchivePage.jsx
// Main problem list page. Filters are lifted to App.jsx and passed down
// so pagination state persists when navigating back from a problem.

import React from "react";
import { TOPICS, DIFFICULTIES, COMPANY_META, PROBLEMS_PER_PAGE } from "../data/constants";
import { useTheme } from "../context/ThemeContext";
import ProblemCard from "../components/ProblemCard";
import Pagination  from "../components/atoms/Pagination";
import "./ArchivePage.css";

export default function ArchivePage({
  problems, meta, loading, error, onRetry, filters, setFilters, nav, setProblem,
}) {
  const { t } = useTheme();
  const hasFilters = filters.search || filters.topic !== "All" || filters.difficulty !== "All" || filters.firm !== "All";

  // Helper: update one filter key and reset to page 1
  const setF = (key, val) =>
    setFilters((f) => ({ ...f, [key]: val, page: key === "page" ? val : 1 }));

  return (
    <>
      {/* ── Hero ── */}
      <section style={{ maxWidth: 1200, margin: "0 auto", padding: "108px 32px 52px" }}>
        <div style={{ maxWidth: 580 }}>
          <span style={{ fontSize: 11, color: t.text3, letterSpacing: "0.12em", fontWeight: 600, textTransform: "uppercase", display: "block", marginBottom: 16 }}>
            Quantitative & Mathematical Problem Archive
          </span>
          <h1 style={{ fontSize: 50, fontWeight: 800, color: t.text, margin: "0 0 18px", lineHeight: 1.08, fontFamily: "'Playfair Display',Georgia,serif", letterSpacing: "-0.03em" }}>
            Problems for the<br />
            <em style={{ fontStyle: "italic", color: t.text2 }}>rigorous mind.</em>
          </h1>
          <p style={{ fontSize: 15, color: t.text2, lineHeight: 1.75, margin: "0 0 28px", maxWidth: 450 }}>
            A curated archive of quantitative finance, mathematics, and problem-solving questions from trading interviews and beyond.
          </p>
          <div className="archive-stats" style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {[{ n: meta.total, l: "problems" }, { n: TOPICS.length - 1, l: "topics" }, { n: "13+", l: "firms" }].map(({ n, l }) => (
              <div className="archive-stat" key={l} style={{ background: t.bg2, borderRadius: 9, padding: "9px 16px" }}>
                <span style={{ fontSize: 20, fontWeight: 700, color: t.text }}>{n}</span>
                <span style={{ fontSize: 12, color: t.text3, marginLeft: 5 }}>{l}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Sticky Filters ── */}
      <section style={{ borderTop: `0.5px solid ${t.border}`, borderBottom: `0.5px solid ${t.border}`, background: t.bg, position: "sticky", top: 56, zIndex: 50, backdropFilter: "blur(12px)" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "12px 32px 10px", display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", gap: 9, alignItems: "center", flexWrap: "wrap" }}>
            {/* Search input */}
            <div style={{ position: "relative", flex: 1 }}>
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: t.text3 }}>
                <circle cx="6.5" cy="6.5" r="5" stroke="currentColor" strokeWidth="1.5" />
                <path d="m10.5 10.5 3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <input
                type="search" placeholder="Search by question ID, title, topic, tag, firm..."
                value={filters.search}
                onChange={(e) => setF("search", e.target.value)}
                style={{ width: "100%", boxSizing: "border-box", paddingLeft: 30, height: 34, border: `0.5px solid ${t.border2}`, borderRadius: 8, fontSize: 13, color: t.text, background: t.inputBg, outline: "none", fontFamily: "inherit" }}
              />
            </div>
            {/* Difficulty select */}
            <select value={filters.difficulty} onChange={(e) => setF("difficulty", e.target.value)}
              style={{ height: 34, padding: "0 10px", border: `0.5px solid ${t.border2}`, borderRadius: 8, fontSize: 13, color: t.text, background: t.inputBg, cursor: "pointer", outline: "none", fontFamily: "inherit", flexShrink: 0 }}>
              {DIFFICULTIES.map((d) => <option key={d}>{d}</option>)}
            </select>
            <select value={filters.firm} onChange={(e) => setF("firm", e.target.value)}
              aria-label="Filter by firm"
              style={{ height: 34, padding: "0 10px", border: `0.5px solid ${t.border2}`, borderRadius: 8, fontSize: 13, color: t.text, background: t.inputBg, cursor: "pointer", outline: "none", fontFamily: "inherit", flexShrink: 0, maxWidth: 190 }}>
              <option value="All">All firms</option>
              {Object.keys(COMPANY_META).map((firm) => <option key={firm} value={firm}>{firm}</option>)}
            </select>
          </div>
          {hasFilters && <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button onClick={() => setFilters({ page: 1, topic: "All", difficulty: "All", firm: "All", search: "" })}
              style={{ height: 34, padding: "0 12px", border: `0.5px solid ${t.border}`, borderRadius: 8, background: "transparent", color: t.text2, fontSize: 12, cursor: "pointer", whiteSpace: "nowrap" }}>
              Clear filters
            </button>
          </div>}
          {/* Topic pills */}
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
            {TOPICS.map((tp) => (
              <button key={tp} onClick={() => setF("topic", tp)}
                style={{ padding: "5px 11px", borderRadius: 7, fontSize: 12, fontWeight: filters.topic === tp ? 600 : 400, background: filters.topic === tp ? t.primary : t.cardBg, color: filters.topic === tp ? t.primaryFg : t.text2, border: `0.5px solid ${filters.topic === tp ? t.primary : t.border}`, cursor: "pointer", transition: "all 0.15s" }}>
                {tp}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── List ── */}
      <section style={{ maxWidth: 1200, margin: "0 auto", padding: "32px 32px 80px" }}>
        {/* Result count */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
          <p style={{ fontSize: 13, color: t.text3, margin: 0 }}>
            {meta.total === 0 ? "No results" : (
              <>Showing <strong style={{ color: t.text }}>{(filters.page - 1) * PROBLEMS_PER_PAGE + 1}–{Math.min(filters.page * PROBLEMS_PER_PAGE, meta.total)}</strong> of <strong style={{ color: t.text }}>{meta.total}</strong></>
            )}
          </p>
          <p style={{ fontSize: 12, color: t.text3, margin: 0 }}>{PROBLEMS_PER_PAGE} per page</p>
        </div>

        {/* Loading skeleton */}
        {loading ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {[...Array(5)].map((_, i) => (
              <div key={i} style={{ height: 130, borderRadius: 14, background: t.bg2, border: `0.5px solid ${t.border}`, animation: "pulse 1.4s ease-in-out infinite" }} />
            ))}
            <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }`}</style>
          </div>
        ) : error ? (
          <div style={{ textAlign: "center", padding: "80px 0", color: t.text3 }}>
            <p style={{ fontSize: 15 }}>Problems could not be loaded.</p>
            <p style={{ fontSize: 13, margin: "0 0 18px" }}>{error}</p>
            <button onClick={onRetry}
              style={{ background: t.primary, color: t.primaryFg, border: "none", borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              Try again
            </button>
          </div>
        ) : problems.length === 0 ? (
          <div style={{ textAlign: "center", padding: "80px 0", color: t.text3 }}>
            <p style={{ fontSize: 15 }}>No problems match your filters.</p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {problems.map((p) => (
              <ProblemCard key={p.id || p.problemId} p={p} onClick={(prob) => { setProblem(prob); nav("problem", prob.id || prob.problemId); }} />
            ))}
          </div>
        )}

        {/* Pagination */}
        <Pagination
          cur={filters.page}
          total={meta.pages}
          onChange={(pg) => setF("page", pg)}
        />
      </section>
    </>
  );
}
